import { Link } from "react-router-dom";
import { Shield, FileText, List, Lock, ArrowRight, CheckCircle, Database, Globe, Users, Server, Eye } from "lucide-react";
import TrustLayout, { Section, P, UL } from "../components/TrustLayout";

const versionHistory = [
  { version: "1.0", date: "2 April 2026", notes: "Initial publication of the Technofocus Trust Center." },
];

const legalDocs = [
  {
    icon: FileText,
    title: "Data Processing Addendum",
    desc: "Defines how we process personal data on behalf of customers — roles, safeguards, and contractual obligations.",
    href: "/dpa",
    label: "View DPA",
  },
  {
    icon: List,
    title: "Sub-Processor List",
    desc: "Transparent list of all third-party service providers used to deliver our platforms and services.",
    href: "/subprocessors",
    label: "View Sub-Processors",
  },
  {
    icon: Lock,
    title: "Privacy Policy",
    desc: "Explains how personal data is collected, used, and protected across all Technofocus platforms.",
    href: "/privacy",
    label: "View Privacy Policy",
  },
];

const commitments = [
  { icon: Shield, title: "GDPR Aligned", desc: "Processing personal data in accordance with applicable data protection laws, including GDPR." },
  { icon: Eye, title: "Transparent", desc: "Clear documentation of how and where data is processed, with accessible records." },
  { icon: Server, title: "Secure Infrastructure", desc: "Technical and organizational security measures across all hosted platforms." },
  { icon: Users, title: "Partner Safeguards", desc: "Contractual data protection obligations with all sub-processors and service providers." },
  { icon: Database, title: "Data Minimization", desc: "Collecting only what is necessary for service delivery — no special categories." },
  { icon: Globe, title: "Global Transfers", desc: "International transfers governed by Standard Contractual Clauses (SCCs)." },
];

const trustSections = [
  { id: "commitment", label: "Our Commitment" },
  { id: "platform", label: "How Our Platform Works" },
  { id: "transfers", label: "Data Hosting & Transfers" },
  { id: "what-data", label: "What Data We Process" },
  { id: "partners", label: "How We Work with Partners" },
  { id: "legal-framework", label: "Legal Framework" },
  { id: "security", label: "Security Measures" },
  { id: "retention", label: "Data Retention" },
  { id: "contact", label: "Contact" },
];

export default function TrustCenter() {
  return (
    <TrustLayout
      title="Trust Center"
      lastUpdated="2 April 2026"
      versionHistory={versionHistory}
      sections={trustSections}
      testId="trust-center-page"
    >
      <P>
        At Technofocus, we are committed to maintaining the trust of our customers, partners, and learners by ensuring transparency, security, and responsible handling of personal data. This Trust Center provides an overview of how we collect, process, and protect personal data across our platforms, including LevelUp, Why Microsoft – Compete Enablement for Partners, Skilling-in-a-Box, and associated enablement and event services.
      </P>

      {/* Commitment */}
      <Section id="commitment" title="Our Commitment">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
          {commitments.map((c) => (
            <div key={c.title} className="rounded-xl p-4 border" style={{ backgroundColor: '#F8FFFE', borderColor: '#E2EBE9' }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: '#E5F3F0' }}>
                <c.icon size={16} style={{ color: '#194449' }} />
              </div>
              <p className="text-sm font-semibold text-gray-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{c.title}</p>
              <p className="text-xs text-gray-500 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Platform architecture */}
      <Section id="platform" title="How Our Platform Works">
        <P>Technofocus operates a globally hosted enablement ecosystem.</P>
        <UL items={[
          "Core platform infrastructure is hosted on Microsoft Azure (United States regions).",
          "Additional modules (e.g., content portals, labs, events) may operate on other trusted infrastructure providers.",
          "Authentication is centrally managed, while certain modules store role-based access and usage data.",
        ]} />
        <P>This architecture allows us to deliver scalable, integrated learning and enablement experiences.</P>
      </Section>

      {/* Transfers */}
      <Section id="transfers" title="Data Hosting & International Transfers">
        <P>Personal data processed by Technofocus may be transferred to and processed in multiple jurisdictions, including:</P>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-4">
          {["United States", "Singapore", "India", "United Kingdom"].map(loc => (
            <div key={loc} className="rounded-xl border px-4 py-3 text-center" style={{ borderColor: '#E2EBE9', backgroundColor: '#F8FFFE' }}>
              <Globe size={16} className="mx-auto mb-1.5" style={{ color: '#194449' }} />
              <p className="text-xs font-semibold text-gray-800">{loc}</p>
            </div>
          ))}
        </div>
        <P>These transfers are governed by Standard Contractual Clauses (SCCs), contractual data protection agreements with sub-processors, and industry-standard security controls. Technofocus operates a unified global platform and does not currently maintain region-specific hosting environments.</P>
      </Section>

      {/* What data */}
      <Section id="what-data" title="What Data We Process">
        <P>We process limited personal data necessary for service delivery, including:</P>
        <UL items={[
          "Name and email address",
          "Organization name",
          "Job role (where applicable)",
          "Course enrollment and completion data",
          "Platform usage data",
          "Event participation data",
        ]} />
        <div className="mt-4 rounded-xl border px-5 py-4 flex items-start gap-3" style={{ borderColor: '#D4EC8E', backgroundColor: '#F9FFED' }}>
          <CheckCircle size={16} className="shrink-0 mt-0.5" style={{ color: '#07641D' }} />
          <p className="text-sm text-gray-700">We do <strong>not</strong> intentionally process special categories of personal data (e.g., health, biometric, or sensitive data).</p>
        </div>
      </Section>

      {/* Partners */}
      <Section id="partners" title="How We Work with Partners">
        <P>In certain programs, Technofocus may share limited learner data with sponsoring organizations (e.g., Microsoft) and authorized collaboration platforms (e.g., Skilling Hub). This data sharing is strictly limited to:</P>
        <UL items={[
          "Program administration and operations",
          "Reporting and analytics for program sponsors",
          "Certification and completion tracking",
        ]} />
      </Section>

      {/* Legal framework */}
      <Section id="legal-framework" title="Legal Framework">
        <P>To ensure compliance and transparency, we maintain the following legal documents:</P>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
          {legalDocs.map(doc => (
            <Link
              key={doc.href}
              to={doc.href}
              data-testid={`trust-doc-${doc.title.toLowerCase().replace(/\s/g, '-')}`}
              className="group rounded-2xl border p-5 block hover:shadow-md transition-all hover:-translate-y-0.5"
              style={{ borderColor: '#E2EBE9', backgroundColor: '#F8FFFE' }}
            >
              <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: '#E5F3F0' }}>
                <doc.icon size={18} style={{ color: '#194449' }} />
              </div>
              <p className="font-semibold text-gray-900 text-sm mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{doc.title}</p>
              <p className="text-xs text-gray-500 leading-relaxed mb-3">{doc.desc}</p>
              <span className="inline-flex items-center gap-1 text-xs font-semibold group-hover:gap-1.5 transition-all" style={{ color: '#194449' }}>
                {doc.label} <ArrowRight size={11} />
              </span>
            </Link>
          ))}
        </div>
      </Section>

      {/* Security */}
      <Section id="security" title="Security Measures">
        <P>Technofocus implements appropriate technical and organizational measures, including:</P>
        <UL items={[
          "Encryption in transit (TLS) across all platforms",
          "Encryption at rest (where supported by infrastructure providers)",
          "Role-based access control (RBAC)",
          "Least-privilege administrative access",
          "Monitoring and logging of system activity",
          "Secure cloud infrastructure (Microsoft Azure and equivalent providers)",
        ]} />
      </Section>

      {/* Retention */}
      <Section id="retention" title="Data Retention">
        <P>Personal data is retained only as long as necessary to deliver services, fulfill contractual obligations, and meet legal requirements. Inactive accounts and outdated records are periodically archived or deleted in accordance with our data retention policies.</P>
      </Section>

      {/* Contact */}
      <Section id="contact" title="Contact Us">
        <P>For any data protection, privacy, or security-related queries, please contact:</P>
        <div className="rounded-xl border p-5 inline-block mt-1" style={{ borderColor: '#E2EBE9', backgroundColor: '#F8FFFE' }}>
          <p className="text-sm font-semibold text-gray-900 mb-1">Data Protection</p>
          <a href="mailto:privacy@technofocus.ai" className="text-sm font-medium hover:underline" style={{ color: '#194449' }}>
            privacy@technofocus.ai
          </a>
        </div>
      </Section>

    </TrustLayout>
  );
}
